import React, { useEffect } from 'react';
import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import { User } from '@court-sync/shared';
import { isWebSentryEnabled } from '../lib/sentry';
import { AuthService } from '../services/auth';
import { Region } from '../services/regionService';
import { regionService } from '../services/regionService';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
  region: Region;

  // Email/Password methods
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string, role?: 'guardian' | 'academy_owner' | 'coach', academyName?: string, returnUrl?: string, legalAcceptance?: { termsAccepted: boolean; privacyAccepted: boolean; termsVersion: string; privacyVersion: string; marketingEmailConsent?: boolean }, country?: string, stateOrProvince?: string) => Promise<{ user: User; needsVerification: boolean; verificationEmailFailed?: boolean }>;
  logout: () => Promise<void>;

  // Social login methods
  loginWithGoogle: (role?: 'guardian' | 'academy_owner' | 'coach') => Promise<User>;
  loginWithFacebook: (role?: 'guardian' | 'academy_owner' | 'coach') => Promise<User>;
  loginWithGithub: (role?: 'guardian' | 'academy_owner' | 'coach') => Promise<User>;

  // User management
  updateUser: (userData: Partial<User>) => Promise<void>;
  refreshUser: (options?: { forceTokenRefresh?: boolean }) => Promise<void>;

  // Region management
  setRegion: (region: Region) => Promise<void>;
  detectRegion: () => Promise<void>;

  // State management
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isLoading: false,
  isAuthenticated: false,
  isInitialized: false,
  region: 'US', // Default to US

  setUser: (user) => {
    set({ user, isAuthenticated: !!user });

    // Set user context in Sentry (no PII - only identifiers)
    if (isWebSentryEnabled()) {
      if (user) {
        Sentry.setUser({
          id: user.id || user.uid,
          academyId: user.academyId,
          // Note: Not including email, username, or other PII for privacy
        });
      } else {
        Sentry.setUser(null);
      }
    }
  },

  setLoading: (isLoading) => {
    set({ isLoading });
  },

  setInitialized: (isInitialized) => {
    set({ isInitialized });
  },

  // Email/Password Authentication
  login: async (email: string, password: string) => {
    set({ isLoading: true });
    try {
      const user = await AuthService.signInWithEmail(email, password);
      set({ user, isAuthenticated: true, isLoading: false });

      // Detect region based on location (fire-and-forget)
      useAuthStore.getState().detectRegion();
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  register: async (email: string, password: string, displayName?: string, role?: 'guardian' | 'academy_owner' | 'coach', academyName?: string, returnUrl?: string, legalAcceptance?: { termsAccepted: boolean; privacyAccepted: boolean; termsVersion: string; privacyVersion: string; marketingEmailConsent?: boolean }, country?: string, stateOrProvince?: string) => {
    set({ isLoading: true });
    try {
      const result = await AuthService.signUpWithEmail(email, password, displayName ?? '', role, academyName, returnUrl, legalAcceptance, country, stateOrProvince);

      // Allow users to proceed even if email verification is needed
      // They can complete payment first, then verify email after
      set({ user: result.user, isAuthenticated: true, isLoading: false });

      // Detect region based on location (fire-and-forget, not blocking registration)
      useAuthStore.getState().detectRegion();

      return result;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await AuthService.signOut();
      const { useImpersonationStore } = await import('./impersonation');
      useImpersonationStore.getState().clearImpersonation();
      set({ user: null, isAuthenticated: false, isLoading: false });

      // Clear Sentry user context
      if (isWebSentryEnabled()) {
        Sentry.setUser(null);
      }

      // Clear all stores on logout
      const { clearAllStores } = await import('./utils');
      clearAllStores();
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // Social Authentication
  loginWithGoogle: async (role?: 'guardian' | 'academy_owner' | 'coach') => {
    set({ isLoading: true });
    try {
      const user = await AuthService.signInWithGoogle(role);
      set({ user, isAuthenticated: true, isLoading: false });

      // Detect region based on location (fire-and-forget)
      useAuthStore.getState().detectRegion();

      return user;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  loginWithFacebook: async (role?: 'guardian' | 'academy_owner' | 'coach') => {
    set({ isLoading: true });
    try {
      const user = await AuthService.signInWithFacebook(role);
      set({ user, isAuthenticated: true, isLoading: false });

      // Detect region based on location (fire-and-forget)
      useAuthStore.getState().detectRegion();

      return user;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  loginWithGithub: async (role?: 'guardian' | 'academy_owner' | 'coach') => {
    set({ isLoading: true });
    try {
      const user = await AuthService.signInWithGithub(role);
      set({ user, isAuthenticated: true, isLoading: false });

      // Detect region based on location (fire-and-forget)
      useAuthStore.getState().detectRegion();

      return user;
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // User management
  updateUser: async (userData: Partial<User>) => {
    set({ isLoading: true });
    try {
      // Import userService dynamically to avoid circular dependency
      const { userService } = await import('../services/user');

      // Call the API to update user profile
      const currentUser = useAuthStore.getState().user;
      await userService.updateProfile({
        firstName: userData.firstName ?? currentUser?.firstName ?? '',
        lastName: userData.lastName ?? currentUser?.lastName ?? '',
        email: userData.email ?? currentUser?.email ?? '',
        phone: userData.phone ?? currentUser?.phone,
        avatarUrl: userData.avatarUrl,
      });

      // Update local state with the new data
      if (currentUser) {
        const updatedUser = { ...currentUser, ...userData };
        set({ user: updatedUser, isLoading: false });
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  refreshUser: async (options) => {
    set({ isLoading: true });
    try {
      const refreshedUser = await AuthService.refreshUserProfile(options);
      set({ user: refreshedUser, isLoading: false });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  // Region management
  setRegion: async (region: Region) => {
    try {
      regionService.setRegion(region);
      set({ region });

      // If user is logged in, update their preferences on backend
      const currentUser = get().user;
      if (currentUser) {
        const { userService } = await import('../services/user');
        await userService.updateRegion(region);
      }
    } catch (error) {
      console.error('Failed to set region:', error);
    }
  },

  detectRegion: async () => {
    try {
      const detectedRegion = await regionService.detectRegion();
      console.log('Detected region:', detectedRegion);
      set({ region: detectedRegion });
    } catch (error) {
      console.error('Failed to detect region:', error);
    }
  },

}));

// Auth Provider Component
interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setUser, setInitialized } = useAuthStore();

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;
    /** Skip query/store reset on the initial anonymous auth event — only clear after logout. */
    let hadAuthenticatedUser = false;

    const init = async () => {
      try {
        await AuthService.completeSocialAuthRedirectIfNeeded();
      } catch (error) {
        console.error('Social auth redirect completion failed:', error);
      }

      if (cancelled) return;

      unsubscribe = AuthService.onAuthStateChanged(async (user) => {
        setUser(user);

        if (user) {
          hadAuthenticatedUser = true;
          const { preloadUserData } = await import('./preload');
          await preloadUserData(user);
        } else if (hadAuthenticatedUser) {
          hadAuthenticatedUser = false;
          const { clearAllStores } = await import('./utils');
          clearAllStores();
        }

        // Mark initialized AFTER preload completes so ProtectedRoute
        // keeps the spinner until currentAcademy is set
        setInitialized(true);
      });
    };

    init();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [setUser, setInitialized]);

  return <>{children}</>;
};